import { useMutation } from '@apollo/client';

import { bcValidator } from '../validation/validator';
import { apolloClientContracts } from '@/apollo/clients/contracts.apollo';
import GetContractDetailsQuery from '@/graphql/contracts/getContractDetails.query.gql';
import UpdateContractLineMutation from '@/graphql/contracts/updateContractLine.mutation.gql';
import { notificationService } from '@/services/notification/notification.client';
import {
  ALL_ITEMS,
} from '@/config/common.config';
import { ContractLineBillingCycleOutput } from '@/graphql/contracts/_generated_/contracts';
import { triggerLineRefresh } from '../components/ContractLinesGrid/linesSelectionObservable';
import {
  UpdateContractLine,
  UpdateContractLineVariables,
} from '@/graphql/contracts/_generated_/updateContractLine.mutation';
import GetPaginatedFilteredBCContractLinesQuery
  from '@/graphql/contracts/getPaginatedFilteredBCContractLines.query.gql';
import {
  GetPaginatedFilteredBCContractLinesVariables,
} from '@/graphql/contracts/_generated_/getPaginatedFilteredBCContractLines.query';

export interface BCContractLinesInputFields {
  stockCode?: string;
  comments?: string;
  quantity?: number;
  unitPrice?: number;
  unitPriceFrequency?: string;
  billingType?: string;
  billingInterval?: string;
  lineStartDate?: Date;
  salesHandoverNumber?: string;
  changeReason?: {
    name: string;
    comment?: string;
  };
  unitType?: string;
}

export function useUpdateFullBCContractLine(contractId: UUID) {
  const [_updateContractLine] = useMutation<
  UpdateContractLine,
  UpdateContractLineVariables
  >(UpdateContractLineMutation, {
    client: apolloClientContracts,
    onError: (err) => {
      notificationService.open({
        title: 'Error',
        text: err.message,
        type: 'danger',
      });
      apolloClientContracts.refetchQueries({
        include: [GetPaginatedFilteredBCContractLinesQuery, GetContractDetailsQuery],
      });
      triggerLineRefresh();
    },
    onCompleted: () => {
      triggerLineRefresh();
    },
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: GetContractDetailsQuery,
        variables: {
          contractId,
        },
      },
      {
        query: GetPaginatedFilteredBCContractLinesQuery,
        variables: ({
          filter: {
            contractId: { eq: contractId },
          },
          first: ALL_ITEMS,
        } as GetPaginatedFilteredBCContractLinesVariables)
      },
    ],
  });

  const updateFullBCContractLine = (
    cl: ContractLineBillingCycleOutput,
    changedField: BCContractLinesInputFields,
    validationContext?: object
  ) => {
    const isValidContractLine = bcValidator.isValidContractLine(
      cl,
      validationContext
    );

    if (isValidContractLine) {
      _updateContractLine({
        variables: {
          stockCode: cl.stockCode,
          comments: cl.comments,
          quantity: cl.quantity,
          unitPrice: cl.unitPrice,
          billingType: cl.billingType!.toString(),
          unitPriceFrequency: cl.unitPriceFrequency,
          billingInterval: cl.billingInterval,
          lineStartDate: cl.lineStartDate,
          changeReason: cl.statusChangeReason
            ? { name: cl.statusChangeReason!, comment: cl.statusChangeComment }
            : null,
          salesHandoverNumber: cl.salesHandoverNumber,
          minCharge: cl.minCharge,
          contractID: contractId,
          contractLineID: cl.id,
          ...changedField,
        },
      });
    }
  };

  return updateFullBCContractLine;
}
